import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MdLink } from 'react-icons/md';
import { IoMdCalendar } from 'react-icons/io';
import { useTranslation } from 'react-i18next';

import config from 'config';

import { showModal } from 'legacy/actions/modalActions';
import { copyToClipboard } from 'legacy/common/utils/clipboard';

import InviteGuestsFromHostedEvents from '../inviteGuestsFromHostedEvents/InviteGuestsFromHostedEvents';
import InviteGroup from '../inviteGroup/InviteGroup';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';

type ShareEventProps = {
  eventId: string;
  shareToken: string;
  isPrivate: boolean;
  isOrganizer: boolean;
  visible: boolean;
  onClose: () => void;
};

const ShareEvent: React.FC<ShareEventProps> = ({
  eventId,
  shareToken,
  isPrivate,
  isOrganizer,
  onClose,
  visible
}) => {
  const dispatch = useDispatch();
  const flags = useFlags();
  const { t } = useTranslation(['common']);
  const [
    showInviteGuestsFromHostedEventsModal,
    setShowInviteGuestsFromHostedEventsModal
  ] = useState(false);

  const [showInviteGroupModal, setShowInviteGroupModal] = useState(false);

  // Do not show share button for guests when the event is private
  if (isPrivate && !isOrganizer) {
    return null;
  }

  let options = [
    {
      id: 'copy-url',
      icon: <MdLink size={24} />,
      label: t('common:buttons.copyUrl'),
      onClick: () => {
        let url = `${config.SITE_URL}events/${eventId}`;

        if (shareToken) {
          url += `?invite=${shareToken}`;
        }

        copyToClipboard(url);
      }
    },
    {
      id: 'invite-bounce',
      icon: (
        <img src="/img/icons/bounce_ball.svg" className="w-[20px] h-[20px]" />
      ),
      label: t('common:buttons.inviteBounceUsers'),
      onClick: () => {
        dispatch(
          showModal('INVITE_USERS_TO_EVENT', {
            _id: eventId
          })
        );
      }
    }
  ];

  if (isOrganizer) {
    options = [
      ...options,
      {
        id: 'invite-hosted',
        icon: <IoMdCalendar size={24} />,
        label: t('common:buttons.inviteFromHostedEvent'),
        onClick: () => {
          setShowInviteGuestsFromHostedEventsModal(true);
        }
      }
    ];
  }

  options = [
    ...options,
    {
      id: 'invite-org',
      icon: <IoMdCalendar size={24} />,
      label: t('common:buttons.inviteOrganization'),
      onClick: () => {
        setShowInviteGroupModal(true);
      }
    }
  ];

  return (
    <>
      <Modal
        type={ModalType.SMALL}
        onClose={onClose}
        autoHeight
        hideCloseButton
        visible={visible}
      >
        <div className="px-4 pt-6 flex flex-col gap-4">
          {options.map((option) => (
            <Button
              type={ButtonType.Inverted}
              key={option.id}
              icon={option.icon}
              onClick={() => {
                onClose();
                option.onClick();
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </Modal>

      {showInviteGuestsFromHostedEventsModal && (
        <InviteGuestsFromHostedEvents
          destinationEventId={eventId}
          onClose={() => {
            setShowInviteGuestsFromHostedEventsModal(false);
          }}
        />
      )}

      {flags[FLAGS.ENABLE_GROUPS] && showInviteGroupModal && (
        <InviteGroup
          eventId={eventId}
          onClose={() => {
            setShowInviteGroupModal(false);
          }}
        />
      )}
    </>
  );
};

export default ShareEvent;
